import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react';

/**
 * The small set of primitives the admin actually needs, in the shadcn/ui style
 * (own the components, no runtime dependency). The Angular admin mixed Angular
 * Material and PrimeNG, so two design systems fought over every screen.
 */

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' }) {
  const styles = {
    primary: 'bg-brand text-ink hover:opacity-90',
    ghost: 'border border-white/15 text-white hover:bg-white/5',
    danger: 'border border-red-500/40 text-red-400 hover:bg-red-500/10',
  }[variant];

  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${styles} ${className}`}
    />
  );
}

// ComponentProps rather than InputHTMLAttributes so `ref` is included: React 19
// passes it as an ordinary prop to function components, and the modal focuses
// its search box on open.
export function Input({
  invalid,
  className = '',
  ...props
}: ComponentProps<'input'> & { invalid?: boolean }) {
  return (
    <input
      {...props}
      aria-invalid={invalid || undefined}
      className={`w-full rounded-md border bg-white/[0.04] px-3 py-2 text-white outline-none transition-colors placeholder:text-white/30 focus:border-brand ${
        invalid ? 'border-red-500' : 'border-white/15'
      } ${className}`}
    />
  );
}

export function Select({
  invalid,
  className = '',
  children,
  ...props
}: ComponentProps<'select'> & { invalid?: boolean }) {
  return (
    <select
      {...props}
      aria-invalid={invalid || undefined}
      // `[&>option]:bg-ink` because a native dropdown's list is painted by the
      // OS, not the page: without it the options render as dark text on a dark
      // popup in a dark-themed browser and are unreadable while open.
      className={`w-full rounded-md border bg-white/[0.04] px-3 py-2 text-white outline-none transition-colors focus:border-brand disabled:opacity-40 [&>option]:bg-ink ${
        invalid ? 'border-red-500' : 'border-white/15'
      } ${className}`}
    >
      {children}
    </select>
  );
}

export function Textarea({
  invalid,
  className = '',
  ...props
}: ComponentProps<'textarea'> & { invalid?: boolean }) {
  return (
    <textarea
      {...props}
      aria-invalid={invalid || undefined}
      className={`w-full rounded-md border bg-white/[0.04] px-3 py-2 text-white outline-none transition-colors placeholder:text-white/30 focus:border-brand ${
        invalid ? 'border-red-500' : 'border-white/15'
      } ${className}`}
    />
  );
}

/**
 * Label + control + optional hint/error, with the label wired to the control.
 *
 * The existing pages hand-roll this triple per field, which is fine for a form
 * with four inputs; the vehicle editor has thirty, and a hand-rolled `htmlFor`
 * that doesn't match its input is an accessibility bug you cannot see.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
  className = '',
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="text-sm text-white/80">
        {label}
        {required && <span className="ml-0.5 text-brand">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && !error && <p className="mt-1 text-xs text-white/40">{hint}</p>}
      {error && (
        <p role="alert" className="mt-1 text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

/** Small status pill — the vehicle list shows draft/active/reserved/sold at a glance. */
export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'success' | 'warning' | 'muted';
  children: ReactNode;
}) {
  const tones = {
    neutral: 'bg-white/10 text-white/70',
    success: 'bg-brand/20 text-brand',
    warning: 'bg-amber-500/20 text-amber-300',
    muted: 'bg-white/5 text-white/40',
  }[tone];

  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${tones}`}>
      {children}
    </span>
  );
}

// Forwards the remaining div props so callers can attach a data-testid: the
// E2E suite needs to address one card rather than "any div containing this
// text", which resolves to the whole list container.
export function Card({
  children,
  className = '',
  ...props
}: ComponentProps<'div'> & { children: ReactNode }) {
  return (
    <div {...props} className={`rounded-lg border border-white/10 bg-white/[0.02] ${className}`}>
      {children}
    </div>
  );
}

export function PageHeader({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="mb-6 flex items-center justify-between gap-4">
      <h1 className="text-2xl font-semibold">{title}</h1>
      {action}
    </div>
  );
}

/**
 * Every async screen shows one of: loading, error, empty, or content.
 * The Angular admin rendered an empty table for all four, so "no jobs yet" and
 * "the request failed" looked identical.
 */
export function QueryState({
  isLoading,
  error,
  isEmpty,
  emptyText,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  isEmpty?: boolean;
  emptyText?: string;
  children: ReactNode;
}) {
  if (isLoading) {
    return <p className="py-12 text-center text-sm text-white/40">Betöltés…</p>;
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-6 text-center">
        <p className="text-sm text-red-400">Nem sikerült betölteni az adatokat.</p>
        <p className="mt-1 text-xs text-white/40">{(error as Error).message}</p>
      </div>
    );
  }
  if (isEmpty) {
    return <p className="py-12 text-center text-sm text-white/40">{emptyText ?? 'Nincs adat.'}</p>;
  }
  return <>{children}</>;
}

/** Confirm before anything irreversible — the admin deletes real records. */
export function ConfirmButton({
  onConfirm,
  confirmText,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  onConfirm: () => void;
  confirmText: string;
  children: ReactNode;
}) {
  return (
    <Button
      {...props}
      variant="danger"
      onClick={() => {
        if (window.confirm(confirmText)) onConfirm();
      }}
    >
      {children}
    </Button>
  );
}
